/**
 * Adobe Analytics — click-level tracking (`ctaClick`) i odsłony sekcji one-pagera (`pageLoad`).
 *
 * Bazowy `pageLoad` leci serwerowo z `<head>` (blok `analytics_data_layer` w base.html.twig,
 * dane z App\Service\PageInfoProvider). Tutaj dokładamy to, czego serwer nie widzi: przejścia
 * między sekcjami strony głównej i otwarcia modali — patrz SECTION_MAP / MODAL_MAP na końcu pliku.
 *
 * Kontrakt wg „Adobe_analytics_Click level_Implementation guide_Lottery Campaign" (v1, 07-08-2026):
 *
 *   adobeDataLayer.push({
 *     event: 'ctaClick',
 *     clickInfo: {
 *       ctaName:     '<name-of-button-or-link>',
 *       ctaType:     'Button' | 'Link' | 'Tab' | 'Accordion' | 'Icon',
 *       ctaLocation: 'Header' | 'Footer' | 'Body' | 'Banner' | 'Carousel',
 *       ctaTitle:    '<title-of-element>'   // opcjonalne — nagłówek sekcji/karuzeli
 *     }
 *   });
 *
 * Co i gdzie łapiemy opisuje CTA_MAP poniżej — selektor + ctaType + ctaLocation + ctaTitle.
 * Nazwy (`ctaName`) NIE stoją w tej mapie: siedzą w `data-cta-name` w Twigach, obok polskich
 * etykiet. Wyjątkiem jest FORM_CTA_MAP na końcu pliku — tam nazwa jest przypięta do zdarzenia
 * formularza, nie do elementu, więc nie ma czego oznaczyć atrybutem.
 * Listener jest delegowany na `document`, więc łapie też elementy dorysowane później
 * (modale, kalendarz laureatów renderowany JS-em).
 *
 * WYJĄTEK: przyciski wysyłki formularzy (rejestracja, kontakt) NIE lecą na klik — dopiero po
 * udanej wysyłce, z FORM_CTA_MAP w drugiej części pliku. Uzasadnienie tam.
 *
 * NAZWY PO ANGIELSKU. Guide wymaga, żeby wartości `ctaName`/`ctaTitle` były angielskie, a UI jest
 * polskie — więc etykiety NIE są czytane z DOM-u. Źródła nazwy, w kolejności:
 *   1. `data-cta-name` na klikniętym elemencie (albo na jego przodku) — tak opisujemy pozycje
 *      generowane w pętli w Twigach (menu, stopka, FAQ, modale); angielska nazwa stoi obok
 *      polskiej etykiety w tym samym miejscu, więc nie da się dodać pozycji i zapomnieć o niej.
 *   2. `name` z CTA_MAP — stała albo funkcja (el) => string dla elementów rysowanych JS-em.
 *   3. Etykieta z DOM-u — wyłącznie awaryjnie, gdy ktoś doda element bez wpisu i bez atrybutu.
 *      W konsoli leci wtedy ostrzeżenie, bo do Adobe poszłaby polska nazwa.
 * `ctaTitle` analogicznie: `data-cta-title` (na elemencie albo na modalu) → `title` z CTA_MAP.
 * Treść pytań FAQ pochodzi z bazy i nie ma angielskiego odpowiednika — jedzie jako
 * „FAQ Question <n>" (pozycja z panelu), patrz templates/sections/faq.html.twig.
 */

// jQuery tylko dla zdarzeń formularzy (`submit-success` to zdarzenie jQuery, native
// addEventListener go nie zobaczy) — patrz sekcja FORM_CTA_MAP.
import $ from 'jquery';

const EVENT_NAME = 'ctaClick';

// Tablicę inicjalizuje już blok `analytics_data_layer` w `<head>`; ten guard zostaje na wypadek
// szablonu, który nie dziedziczy z base.html.twig. Pushe sprzed załadowania embeda Launcha
// nie giną — AEP Data Layer po inicjalizacji odtwarza to, co już w tablicy leży.
window.adobeDataLayer = window.adobeDataLayer || [];

// Kolejność ma znaczenie: wygrywa pierwszy pasujący wpis (najbardziej szczegółowy na górze).
const CTA_MAP = [
    // ---------------------------------------------------------------- Header
    // ctaName z data-cta-name (site-header.html.twig): „Awards", „How to Participate", „Form",
    // „Contact", „Statute" (menu), „Take part" (CTA), „Mastercard Logo", „Menu" (burger).
    { selector: '.site-header__cta, .site-header__panel-cta', type: 'Button', location: 'Header' },
    { selector: '.site-header__nav a, .site-header__menu a', type: 'Button', location: 'Header' },
    { selector: '.site-header__logo', type: 'Icon', location: 'Header' },
    { selector: '.site-header__burger', type: 'Icon', location: 'Header' },

    // ------------------------------------------------------------------ Hero
    // Guide, przykład 2: ctaLocation „Body", ctaTitle = nagłówek hero po angielsku.
    // ctaName z data-cta-name (hero.html.twig): „Take part in the lottery", „Wakacje.pl".
    { selector: '.s-hero__cta', type: 'Button', location: 'Body', title: 'Pay with Mastercard and win!' },
    { selector: '.s-hero__note a', type: 'Link', location: 'Body', title: 'Pay with Mastercard and win!' },

    // ---------------------------------------------------------- Rejestracja
    // Uwaga: przycisku „Go to verification" tu NIE ma — jako jedyny CTA sekcji leci z
    // FORM_CTA_MAP (na końcu pliku), dopiero po udanej wysyłce formularza.
    // Poniższy wpis to przypis POD formularzem — ctaName z data-cta-name
    // (registration.html.twig): „Contact E-mail", „Download Guardian Consent Template".
    { selector: '.s-registration__footnote a', type: 'Link', location: 'Body', title: 'Register for the lottery!' },

    // -------------------------------------------------------------- Kontakt
    // Przycisku „Send" tu nie ma — jak wyżej, patrz FORM_CTA_MAP. Wpis łapie ewentualne
    // linki w treści sekcji (dziś ich nie ma, sekcja to sam formularz).
    { selector: '.s-contact a', type: 'Link', location: 'Body', title: 'If you have any more questions, contact us' },

    // ------------------------------------------------------------------ FAQ
    // ctaName z data-cta-name (faq.html.twig): „FAQ Question <n>" — treść pytań jest z bazy.
    { selector: '.s-faq__q', type: 'Accordion', location: 'Body', title: 'Answers to the most frequently asked questions' },

    // ------------------------------------------------------------- Laureaci
    // Strzałki: ctaName z data-cta-name (winners.html.twig) — „Previous Month" / „Next Month".
    // Dni rysuje assets/js/winners-section.js, więc nazwę składamy tutaj z daty ISO
    // (język-neutralna), a nie z widocznej liczby — inaczej nie odróżnisz miesięcy.
    { selector: '.s-winners__nav', type: 'Icon', location: 'Body', title: 'Winners' },
    { selector: '.s-winners__day', name: (el) => `Winners Day ${el.dataset.date}`, type: 'Tab', location: 'Body', title: 'Winners' },

    // --------------------------------------------------------------- Modale
    // ctaName z data-cta-name na przycisku („Close" / „Back to the form"), a ctaTitle
    // z data-cta-title na .modal — nagłówek w DOM-ie jest polski, więc go nie czytamy.
    { selector: '.registration-modal__btn', type: 'Button', location: 'Body' },

    // ------------------------------------------------- Podstrony dokumentowe
    // Linki w treści polityki prywatności; ctaName z data-cta-name (privacy-policy.html.twig):
    // „Statute", „Manage Cookies", „Cookie Settings", „Contact Form", „DPO E-mail".
    { selector: '.s-legal a', type: 'Link', location: 'Body', title: 'Privacy Policy' },

    // --------------------------------------------------------------- Stopka
    // ctaName z data-cta-name (site-footer.html.twig): „Statute", „Edenred Card Terms",
    // „Wakacje.pl Voucher Terms", „Privacy Policy", „Website Terms of Use", „Contact",
    // „Manage Cookies", „Mastercard Logo".
    { selector: '.site-footer__links a', type: 'Link', location: 'Footer' },
    { selector: '.site-footer__logo', type: 'Icon', location: 'Footer' },
];

/**
 * Awaryjna etykieta z DOM-u — polska, więc używana tylko wtedy, gdy element nie ma
 * `data-cta-name` ani wpisu `name` w CTA_MAP. `textContent` zamiast `innerText`, bo działa też
 * na elementach ukrytych (panel mobilnego menu ma `display: none` do czasu otwarcia).
 */
function labelOf(el) {
    const text = (el.textContent || '').replace(/\s+/g, ' ').trim();

    return text || el.getAttribute('aria-label') || el.getAttribute('title') || '';
}

/**
 * Angielska nazwa CTA. `closest` zamiast samego `dataset`, bo kliknięcie trafia w element
 * dopasowany selektorem, ale atrybut może siedzieć na jego przodku (np. link owinięty w <li>).
 */
function nameOf(entry, el) {
    const marked = el.closest('[data-cta-name]');

    if (marked) {
        return marked.dataset.ctaName;
    }

    if (typeof entry.name === 'function') {
        return entry.name(el);
    }

    if (entry.name) {
        return entry.name;
    }

    const fallback = labelOf(el);

    if (fallback) {
        // Bez tego do Adobe poleciałaby polska nazwa, a guide wymaga angielskiej.
        console.warn(`[tracking] brak data-cta-name — ctaName po polsku: "${fallback}"`, el);
    }

    return fallback;
}

/**
 * Angielski `ctaTitle`. Modal Bootstrapa trzyma go w `data-cta-title` na sobie (nagłówek w DOM-ie
 * jest polski), więc „Close" z jednego modala da się odróżnić od „Close" z innego.
 */
function titleOf(entry, el) {
    const marked = el.closest('[data-cta-title]');

    if (marked) {
        return marked.dataset.ctaTitle;
    }

    return entry.title || '';
}

function push(entry, el) {
    const name = nameOf(entry, el);

    if (!name) {
        return;
    }

    const clickInfo = {
        ctaName: name,
        ctaType: entry.type,
        ctaLocation: entry.location,
    };

    const title = titleOf(entry, el);

    if (title) {
        clickInfo.ctaTitle = title;
    }

    window.adobeDataLayer.push({ event: EVENT_NAME, clickInfo });
}

document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) {
        return;
    }

    for (const entry of CTA_MAP) {
        const el = event.target.closest(entry.selector);

        if (el) {
            push(entry, el);

            return;
        }
    }
});

/* ==========================================================================================
 * Formularze — `ctaClick` dopiero po UDANEJ wysyłce.
 *
 * DI zgłosiło przy walidacji (12-08-2026), że event dla „Go to verification" i „Send" leciał
 * na każdy klik, także gdy formularz miał błędy (np. BIN nie należy do Mastercard) — zawyżało to
 * liczbę konwersji. Dlatego te dwa przyciski wypadły z CTA_MAP i słuchamy zdarzenia
 * `submit-success` (assets/js/form/ajax-form.js), które leci wyłącznie po odpowiedzi 2xx, czyli:
 *   - przeszła walidacja HTML5 po stronie klienta (`.needs-validation` robi preventDefault
 *     i zatrzymuje propagację, więc AJAX w ogóle nie wystartuje),
 *   - przeszła walidacja serwerowa (błędy pól wracają jako 4xx → `submit-error`, nie liczymy),
 *   - dla rejestracji: zgłoszenie zostało zapisane i mamy `redirect` na PayU, czyli użytkownik
 *     faktycznie przechodzi do transakcji weryfikacyjnej.
 *
 * Listener jest delegowany na `document`, więc wchodzi PO handlerach podpiętych bezpośrednio
 * pod formularz (handler/contact.js) — jeśli tamten odrzuci nieoczekiwaną odpowiedź serwera
 * (`event.assert`), do trackingu już nie dojdzie.
 * ========================================================================================== */

// Tu — w odróżnieniu od CTA_MAP — nazwa stoi w JS: event jest przypięty do formularza
// (`submit-success`), a nie do klikniętego elementu, więc nie ma czego oznaczyć atrybutem.
const FORM_CTA_MAP = [
    // Guide, przykład 3: ctaName „Go to verification" / ctaTitle „Register for the lottery!".
    { selector: '.registration-form', name: 'Go to verification', title: 'Register for the lottery!' },
    // Guide, przykład 4: ctaName „Send" / ctaTitle „If you have any more questions, contact us".
    { selector: '.contact-form', name: 'Send', title: 'If you have any more questions, contact us' },
];

for (const entry of FORM_CTA_MAP) {
    $(document).on('submit-success', entry.selector, (event) => {
        const button = event.currentTarget.querySelector('button[type="submit"]');

        if (button) {
            push({ name: entry.name, type: 'Button', location: 'Body', title: entry.title }, button);
        }
    });
}

/* ==========================================================================================
 * Wirtualne odsłony (`pageLoad`) — sekcje strony głównej i modale.
 *
 * Front jest one-pagerem: cała ścieżka użytkownika (zasady → formularz → wynik płatności) siedzi
 * pod `/`, więc bez tego w raportach Adobe byłaby jedna odsłona „Lottery:Home" na całą wizytę.
 * Nazewnictwo trzyma konwencję z App\Service\PageInfoProvider: `Lottery:<sekcja>[:<krok>]`.
 *
 * Kiedy co leci:
 *   - scroll do sekcji  → RAZ na odświeżenie strony (obserwator odpina sekcję po pierwszym
 *     trafieniu). Bez tego limitu przewijanie tam i z powrotem zawyżałoby odsłony kilkukrotnie
 *     i rozwaliło bounce rate.
 *   - klik w nawigację (`hashchange`) i otwarcie modala → za każdym razem, bo to intencjonalne
 *     akcje użytkownika, a nie efekt ubocznego przewijania.
 * `hashchange` dodatkowo odpina sekcję od obserwatora, żeby dojazd scrollem po kliknięciu w menu
 * nie policzył tej samej sekcji dwa razy.
 * ========================================================================================== */

// Sekcje bywają wyższe niż viewport — wtedy `threshold` nigdy nie sięgnie sensownej wartości,
// więc zamiast progu liczymy wejście sekcji w środkowe 50% ekranu.
const SECTION_ROOT_MARGIN = '-25% 0px -25% 0px';

// `#nagrody` celowo nie ma wpisu: to id siedzi na sekcji hero (templates/sections/hero.html.twig),
// czyli klik w „Nagrody" scrolluje na samą górę strony. Osobna odsłona dublowałaby „Lottery:Home"
// z `<head>` na dokładnie tym samym widoku. Sam klik w menu łapiemy wyżej jako `ctaClick`.
const SECTION_MAP = {
    zasady: ['How To Participate', null],
    rejestracja: ['Registration', 'Form'],
    winners: ['Winners', null],
    faq: ['FAQ', null],
    contact: ['Contact', null],
};

// Modale to realne kroki konwersji — klient chce je widzieć w raportach jako osobne odsłony.
const MODAL_MAP = {
    registrationSuccessModal: ['Registration', 'Success'],
    registrationCardModal: ['Registration', 'Card Not Eligible'],
    registrationDataModal: ['Registration', 'Data Mismatch'],
    registrationErrorModal: ['Registration', 'Error'],
    // Niedostępność PayU celowo raportujemy pod istniejącą nazwą `Lottery:Registration:Error`,
    // a nie nową — to dla DI ta sama kategoria (nieudana rejestracja, piksel konwersji NIE leci),
    // więc nie wymaga uzgadniania kolejnej pozycji w tabelce pageName.
    registrationPayuModal: ['Registration', 'Error'],
    contactSuccessModal: ['Contact', 'Success'],
};

function pushPageLoad([section2, section3]) {
    const base = window.adobePageInfo || {};
    const pageInfo = {
        ...base,
        pageName: ['Lottery', section2, section3].filter(Boolean).join(':'),
        siteSection2: section2,
    };

    if (section3) {
        pageInfo.siteSection3 = section3;
    } else {
        delete pageInfo.siteSection3;
    }

    window.adobeDataLayer.push({ event: 'pageLoad', pageInfo });
}

const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
        if (!entry.isIntersecting) {
            continue;
        }

        observer.unobserve(entry.target);
        pushPageLoad(SECTION_MAP[entry.target.id]);
    }
}, { root: null, rootMargin: SECTION_ROOT_MARGIN, threshold: 0 });

for (const id of Object.keys(SECTION_MAP)) {
    const section = document.getElementById(id);

    if (section) {
        observer.observe(section);
    }
}

window.addEventListener('hashchange', () => {
    const id = window.location.hash.slice(1);
    const entry = SECTION_MAP[id];

    if (!entry) {
        return;
    }

    const section = document.getElementById(id);

    if (section) {
        observer.unobserve(section);
    }

    pushPageLoad(entry);
});

document.addEventListener('shown.bs.modal', (event) => {
    const entry = MODAL_MAP[event.target.id];

    if (entry) {
        pushPageLoad(entry);
    }
});
